// Renders the small Markdown used by the legal documents: headings (#, ##, ###), paragraphs,
// bulleted (-) and numbered (1.) lists, **bold**, *italic* and [links](/legal/terms).
// Safe by construction: text becomes React text (no HTML); links only to the site, https or mailto.
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Typography, Link, Box } from '@mui/material';

function inline(text, keyBase) {
  const out = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0; let m; let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyBase}-${i++}`;
    if (m[2]) out.push(<strong key={k}>{m[2]}</strong>);
    else if (m[3]) out.push(<em key={k}>{m[3]}</em>);
    else {
      const href = m[5];
      if (href.startsWith('/')) out.push(<Link key={k} component={RouterLink} to={href}>{m[4]}</Link>);
      else if (/^(https:\/\/|mailto:)/i.test(href)) out.push(<Link key={k} href={href} target="_blank" rel="noopener noreferrer">{m[4]}</Link>);
      else out.push(m[4]);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function MarkdownView({ text, dense = false }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let para = []; let list = null;
  const flushPara = () => { if (para.length) { blocks.push({ type: 'p', text: para.join(' ') }); para = []; } };
  const flushList = () => { if (list) { blocks.push(list); list = null; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    const ul = /^[-*]\s+(.*)$/.exec(line);
    const ol = /^\d+[.)]\s+(.*)$/.exec(line);
    if (h) { flushPara(); flushList(); blocks.push({ type: `h${h[1].length}`, text: h[2] }); }
    else if (ul || ol) {
      flushPara();
      const type = ul ? 'ul' : 'ol';
      if (!list || list.type !== type) { flushList(); list = { type, items: [] }; }
      list.items.push((ul || ol)[1]);
    } else { flushList(); para.push(line); }
  }
  flushPara(); flushList();

  const gap = dense ? 1 : 1.5;
  return (
    <Box>
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <Typography key={i} variant="h5" fontWeight={700} mt={i ? 3 : 0} mb={1}>{inline(b.text, i)}</Typography>;
        if (b.type === 'h2') return <Typography key={i} variant="h6" fontWeight={700} mt={i ? 3 : 0} mb={1}>{inline(b.text, i)}</Typography>;
        if (b.type === 'h3') return <Typography key={i} variant="subtitle1" fontWeight={700} mt={2} mb={0.5}>{inline(b.text, i)}</Typography>;
        if (b.type === 'p') return <Typography key={i} variant="body2" mb={gap} sx={{ lineHeight: 1.7 }}>{inline(b.text, i)}</Typography>;
        const Tag = b.type;
        return (
          <Box key={i} component={Tag} sx={{ pl: 3, mt: 0, mb: gap }}>
            {b.items.map((it, j) => (
              <Typography key={j} component="li" variant="body2" sx={{ lineHeight: 1.7, mb: 0.5 }}>{inline(it, `${i}-${j}`)}</Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
